import React, { useState, useEffect, useCallback } from 'react';
import Button from '../Button';
import Alert from '../Alert';
import ClassRowEditor from './ClassRowEditor';
import TransferMembersModal from './TransferMembersModal';
import EnableClassesModal from './EnableClassesModal';
import DisableClassesModal from './DisableClassesModal';
import {
  getEnterpriseClasses,
  createEnterpriseClass,
  disableEnterpriseClass,
} from '../../api/admin';

/**
 * class-stream A4：超管「团队管理」区（嵌在编辑企业弹窗内）。
 *
 * 用户可见文案一律用「团队」（后端 model 名仍是 class/TrainingClass）。
 *
 * Props:
 *   enterprise  当前编辑的企业 { id, classes_enabled, employee_count, ... }
 *   onChange    任意团队侧改动后回调（父级据此 reload 企业列表，拿到最新 classes_enabled）
 */
export default function AdminEnterpriseClassesSection({ enterprise, onChange }) {
  const entId = enterprise?.id;
  const classesEnabled = !!enterprise?.classes_enabled;

  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  // 弹窗状态
  const [transferFor, setTransferFor] = useState(null); // 源团队行
  const [showEnable, setShowEnable] = useState(false);
  const [showDisable, setShowDisable] = useState(false);

  const loadClasses = useCallback(async () => {
    if (!entId || !classesEnabled) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await getEnterpriseClasses(entId);
      setClasses(data);
    } catch {
      setError('加载团队列表失败');
    } finally {
      setLoading(false);
    }
  }, [entId, classesEnabled]);

  useEffect(() => { loadClasses(); }, [loadClasses]);

  // 团队侧改动后：刷新本区列表 + 通知父级（classes_enabled 等可能变）
  const refresh = () => { loadClasses(); onChange?.(); };

  const active = classes.filter((c) => c.is_active);
  const archived = classes.filter((c) => !c.is_active);

  const handleCreate = async () => {
    const name = window.prompt('新建团队 — 请输入团队名称');
    if (name == null) return;
    if (!name.trim()) { setError('团队名称不能为空'); return; }
    setCreating(true); setError('');
    try {
      await createEnterpriseClass(entId, { name: name.trim() });
      refresh();
    } catch (e) {
      setError(e.response?.data?.detail || '新建团队失败');
    } finally {
      setCreating(false);
    }
  };

  const handleDisable = async (cls) => {
    setError('');
    try {
      await disableEnterpriseClass(entId, cls.id);
      refresh();
    } catch (e) {
      const code = e.response?.data?.error_code;
      setError(
        code === 'members_not_empty'
          ? '请先转走全部成员后再停用该团队。'
          : (e.response?.data?.detail || '停用失败')
      );
    }
  };

  return (
    <div style={sectionStyle}>
      <div style={sectionTitle}>团队管理</div>
      {error && <Alert type="error">{error}</Alert>}

      {!classesEnabled ? (
        // ── 无团队形态 ──
        <div>
          <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px', lineHeight: 1.6 }}>
            该企业当前为<strong>无团队</strong>形态：员工注册后直接绑定企业，看板为全员聚合。
            启用团队后可按团队分流注册、分团队查看看板。
          </p>
          <Button size="sm" onClick={() => setShowEnable(true)}>启用团队</Button>
        </div>
      ) : loading ? (
        <p style={{ color: '#94a3b8', fontSize: 13 }}>加载中...</p>
      ) : (
        // ── 启用团队形态 ──
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <Button size="sm" onClick={handleCreate} loading={creating}>新建团队</Button>
            <Button variant="secondary" size="sm" onClick={() => setShowDisable(true)}>转为无团队企业</Button>
          </div>

          {/* 活跃团队 */}
          <div style={groupLabel}>活跃团队（{active.length}）</div>
          {active.length === 0 ? (
            <p style={emptyHint}>暂无活跃团队。</p>
          ) : (
            <div style={listStyle}>
              {active.map((c) => (
                <ClassRowEditor
                  key={c.id}
                  entId={entId}
                  cls={c}
                  onTransfer={setTransferFor}
                  onDisable={handleDisable}
                  onRenamed={refresh}
                  onError={setError}
                />
              ))}
            </div>
          )}

          {/* 归档团队（只读） */}
          {archived.length > 0 && (
            <>
              <div style={{ ...groupLabel, marginTop: 16 }}>归档团队（{archived.length}）</div>
              <div style={listStyle}>
                {archived.map((c) => (
                  <div key={c.id} style={archivedRow}>
                    <code style={codeStyle}>{c.join_code}</code>
                    <span style={{ fontSize: 14, color: '#64748b', flex: 1 }}>{c.name}</span>
                    <span style={metaStyle}>成员 {c.member_count}</span>
                    <span style={archivedBadge}>归档</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── 弹窗 ── */}
      {transferFor && (
        <TransferMembersModal
          entId={entId}
          sourceClass={transferFor}
          activeClasses={active}
          onClose={() => setTransferFor(null)}
          onDone={() => { setTransferFor(null); refresh(); }}
        />
      )}
      {showEnable && (
        <EnableClassesModal
          entId={entId}
          employeeCount={enterprise?.employee_count ?? 0}
          onClose={() => setShowEnable(false)}
          onDone={() => { setShowEnable(false); refresh(); }}
        />
      )}
      {showDisable && (
        <DisableClassesModal
          entId={entId}
          activeClassCount={active.length}
          onClose={() => setShowDisable(false)}
          onDone={() => { setShowDisable(false); refresh(); }}
        />
      )}
    </div>
  );
}

const sectionStyle = { padding: '14px 16px', background: '#fafbfc', borderRadius: 10, border: '1px solid #e2e8f0' };
const sectionTitle = { fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #e2e8f0' };
const groupLabel = { fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8 };
const listStyle = { display: 'flex', flexDirection: 'column', gap: 8 };
const emptyHint = { fontSize: 12, color: '#94a3b8', margin: 0 };
const archivedRow = { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10 };
const codeStyle = { fontSize: 11, background: '#f1f5f9', padding: '2px 8px', borderRadius: 6, color: '#475569' };
const metaStyle = { fontSize: 12, color: '#94a3b8' };
const archivedBadge = { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#f1f5f9', color: '#94a3b8' };
